// hooks/useRouteSchedulingEnhancements.js
import { useMemo } from 'react';
import { 
  calculateRouteMetrics,
  needsReoptimization,
  filterRoutes,
  groupRoutesByDay
} from '../utils/routeDataUtils';
import { calculateWeeklyHours } from '../components/routeSchedulingHelpers';

/**
 * Enhanced route metrics calculation
 * @param {Array} routes - Array of routes
 * @returns {Object} Detailed metrics by crew, day, and total
 */
export const useRouteMetrics = (routes) => {
  return useMemo(() => {
    if (!routes || routes.length === 0) {
      return {
        byCrewId: {},
        byDay: {},
        total: {
          stopCount: 0,
          totalHours: 0,
          serviceHours: 0,
          driveHours: 0,
          efficiency: 0,
          milesDriven: 0
        }
      };
    }

    const metricsByCrewId = {};
    const metricsByDay = {};
    const totalMetrics = {
      stopCount: 0,
      totalHours: 0,
      serviceHours: 0,
      driveHours: 0,
      efficiencySum: 0,
      routeCount: 0,
      milesDriven: 0
    };

    routes.forEach(route => {
      const metrics = calculateRouteMetrics(route);
      const crewId = route.crewId;
      const day = route.dayOfWeek || route.day;

      // Aggregate by crew
      if (!metricsByCrewId[crewId]) {
        metricsByCrewId[crewId] = {
          routes: 0,
          stopCount: 0,
          totalHours: 0,
          serviceHours: 0,
          driveHours: 0,
          milesDriven: 0,
          efficiency: 0
        };
      }

      const crewMetrics = metricsByCrewId[crewId];
      crewMetrics.routes++;
      crewMetrics.stopCount += metrics.stopCount;
      crewMetrics.totalHours += metrics.totalHours;
      crewMetrics.serviceHours += metrics.serviceHours;
      crewMetrics.driveHours += metrics.driveHours;
      crewMetrics.milesDriven += metrics.milesDriven;

      // Aggregate by day
      if (day) {
        if (!metricsByDay[day]) {
          metricsByDay[day] = {
            routes: 0,
            stopCount: 0,
            totalHours: 0,
            serviceHours: 0,
            driveHours: 0,
            milesDriven: 0,
            efficiency: 0
          };
        }

        const dayMetrics = metricsByDay[day];
        dayMetrics.routes++;
        dayMetrics.stopCount += metrics.stopCount;
        dayMetrics.totalHours += metrics.totalHours;
        dayMetrics.serviceHours += metrics.serviceHours;
        dayMetrics.driveHours += metrics.driveHours;
        dayMetrics.milesDriven += metrics.milesDriven;
      }

      // Aggregate totals
      totalMetrics.stopCount += metrics.stopCount;
      totalMetrics.totalHours += metrics.totalHours;
      totalMetrics.serviceHours += metrics.serviceHours;
      totalMetrics.driveHours += metrics.driveHours;
      totalMetrics.efficiencySum += metrics.efficiency;
      totalMetrics.routeCount++;
      totalMetrics.milesDriven += metrics.milesDriven;
    });

    // Calculate average efficiencies
    Object.values(metricsByCrewId).forEach(metrics => {
      if (metrics.totalHours > 0) {
        metrics.efficiency = Math.round((metrics.serviceHours / metrics.totalHours) * 100);
      }
    });

    Object.values(metricsByDay).forEach(metrics => {
      if (metrics.totalHours > 0) {
        metrics.efficiency = Math.round((metrics.serviceHours / metrics.totalHours) * 100);
      }
    });

    const totalEfficiency = totalMetrics.routeCount > 0
      ? Math.round(totalMetrics.efficiencySum / totalMetrics.routeCount)
      : 0;

    return {
      byCrewId: metricsByCrewId,
      byDay: metricsByDay,
      total: {
        stopCount: totalMetrics.stopCount,
        totalHours: Math.round(totalMetrics.totalHours * 10) / 10,
        serviceHours: Math.round(totalMetrics.serviceHours * 10) / 10,
        driveHours: Math.round(totalMetrics.driveHours * 10) / 10,
        efficiency: totalEfficiency,
        milesDriven: totalMetrics.milesDriven
      }
    };
  }, [routes]);
};

/**
 * Calculate crew workload across a week
 * @param {string} crewId - Crew ID
 * @param {Array} weeklyRoutes - Weekly routes array
 * @returns {Object} Crew workload data
 */
export const useCrewWorkload = (crewId, weeklyRoutes) => {
  return useMemo(() => {
    if (!crewId || !weeklyRoutes) {
      return {
        dailyHours: {},
        weeklyTotal: 0,
        averageDaily: 0,
        peakDay: null,
        lightestDay: null,
        balanceScore: 100 // Perfect balance
      };
    }

    const dailyHours = {};
    const dailyMetrics = {};
    let weeklyTotal = 0;
    let peakDay = { day: null, hours: 0 };
    let lightestDay = { day: null, hours: Infinity };

    // Days of the week
    const weekDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

    weekDays.forEach(dayName => {
      const dayRoute = weeklyRoutes.find(r => r.day === dayName);
      const crewRoute = dayRoute?.routes?.find(r => r.crewId === crewId);

      if (crewRoute) {
        const metrics = calculateRouteMetrics(crewRoute);
        dailyHours[dayName] = metrics.totalHours;
        dailyMetrics[dayName] = metrics;
        weeklyTotal += metrics.totalHours;

        if (metrics.totalHours > peakDay.hours) {
          peakDay = { day: dayName, hours: metrics.totalHours, metrics };
        }
        if (metrics.totalHours < lightestDay.hours) {
          lightestDay = { day: dayName, hours: metrics.totalHours, metrics };
        }
      } else {
        dailyHours[dayName] = 0;
        dailyMetrics[dayName] = null;
        if (0 < lightestDay.hours) {
          lightestDay = { day: dayName, hours: 0, metrics: null };
        }
      }
    });

    const workDays = Object.values(dailyHours).filter(hours => hours > 0).length;
    const averageDaily = workDays > 0 ? weeklyTotal / workDays : 0;

    // Calculate balance score (100 = perfect balance, 0 = very unbalanced)
    let balanceScore = 100;
    if (workDays > 1 && averageDaily > 0) {
      const variance = Object.values(dailyHours)
        .filter(hours => hours > 0)
        .reduce((sum, hours) => sum + Math.pow(hours - averageDaily, 2), 0) / workDays;
      const standardDeviation = Math.sqrt(variance);
      const coefficientOfVariation = standardDeviation / averageDaily;
      balanceScore = Math.max(0, Math.round(100 * (1 - coefficientOfVariation)));
    }

    return {
      dailyHours,
      dailyMetrics,
      weeklyTotal: Math.round(weeklyTotal * 10) / 10,
      averageDaily: Math.round(averageDaily * 10) / 10,
      peakDay: peakDay.day ? peakDay : null,
      lightestDay: lightestDay.hours === Infinity ? null : lightestDay,
      workDays,
      balanceScore
    };
  }, [crewId, weeklyRoutes]);
};

/**
 * Check optimization status across multiple routes
 * @param {Array} routes - Array of routes
 * @param {number} hoursThreshold - Hours since last optimization (default 24)
 * @returns {Object} Optimization status summary
 */
export const useOptimizationStatus = (routes, hoursThreshold = 24) => {
  return useMemo(() => {
    if (!routes || routes.length === 0) {
      return {
        total: 0,
        optimized: 0,
        needsOptimization: 0,
        neverOptimized: 0,
        percentage: 0,
        routesNeedingOptimization: [],
        routesNeverOptimized: []
      };
    }

    const status = routes.reduce((acc, route) => {
      acc.total++;

      if (route.isOptimized) {
        acc.optimized++;
      }

      if (!route.isOptimized || !route.lastOptimized) {
        acc.neverOptimized++;
        acc.routesNeverOptimized.push({
          routeId: route._id,
          crewId: route.crewId,
          day: route.dayOfWeek || route.day,
          stopCount: route.stops?.length || 0
        });
      } else if (needsReoptimization(route, hoursThreshold)) {
        acc.needsOptimization++;
        acc.routesNeedingOptimization.push({
          routeId: route._id,
          crewId: route.crewId,
          day: route.dayOfWeek || route.day,
          lastOptimized: route.lastOptimized,
          hoursSince: Math.round((Date.now() - new Date(route.lastOptimized).getTime()) / (1000 * 60 * 60)),
          stopCount: route.stops?.length || 0
        });
      }

      return acc;
    }, {
      total: 0,
      optimized: 0,
      needsOptimization: 0,
      neverOptimized: 0,
      routesNeedingOptimization: [],
      routesNeverOptimized: []
    });

    status.percentage = status.total > 0
      ? Math.round((status.optimized / status.total) * 100)
      : 0;

    return status;
  }, [routes, hoursThreshold]);
};

/**
 * Calculate weekly summary metrics
 * @param {Array} weeklyRoutes - Weekly routes data
 * @param {Array} crews - Array of crews
 * @returns {Object} Weekly summary
 */
export const useWeeklySummary = (weeklyRoutes, crews) => {
  return useMemo(() => {
    if (!weeklyRoutes || weeklyRoutes.length === 0) {
      return {
        totalStops: 0,
        totalHours: 0,
        totalMiles: 0,
        averageEfficiency: 0,
        busiestDay: null,
        slowestDay: null,
        weeklyHoursByCrew: {},
        routesByDay: {}
      };
    }

    // Group routes by day for easier access
    const routesByDay = groupRoutesByDay(weeklyRoutes);

    // Calculate weekly hours using existing helper
    const weeklyHoursByCrew = calculateWeeklyHours(weeklyRoutes, crews);

    // Calculate daily totals
    let totalStops = 0;
    let totalHours = 0;
    let totalMiles = 0;
    let totalEfficiency = 0;
    let routeCount = 0;
    let busiestDay = { day: null, stops: 0, hours: 0 };
    let slowestDay = { day: null, stops: Infinity, hours: Infinity };

    weeklyRoutes.forEach(dayRoute => {
      const dayName = dayRoute.day;
      let dayStops = 0;
      let dayHours = 0;
      let dayMiles = 0;

      dayRoute.routes?.forEach(route => {
        const metrics = calculateRouteMetrics(route);
        dayStops += metrics.stopCount;
        dayHours += metrics.totalHours;
        dayMiles += metrics.milesDriven;
        totalEfficiency += metrics.efficiency;
        routeCount++;
      });

      totalStops += dayStops;
      totalHours += dayHours;
      totalMiles += dayMiles;

      if (dayStops > busiestDay.stops || (dayStops === busiestDay.stops && dayHours > busiestDay.hours)) {
        busiestDay = { day: dayName, stops: dayStops, hours: dayHours, miles: dayMiles };
      }

      if (dayStops < slowestDay.stops || (dayStops === slowestDay.stops && dayHours < slowestDay.hours)) {
        slowestDay = { day: dayName, stops: dayStops, hours: dayHours, miles: dayMiles };
      }
    });

    const averageEfficiency = routeCount > 0
      ? Math.round(totalEfficiency / routeCount)
      : 0;

    return {
      totalStops,
      totalHours: Math.round(totalHours * 10) / 10,
      totalMiles: Math.round(totalMiles),
      averageEfficiency,
      busiestDay: busiestDay.day ? busiestDay : null,
      slowestDay: slowestDay.stops === Infinity ? null : slowestDay,
      weeklyHoursByCrew,
      routesByDay,
      averageStopsPerDay: weeklyRoutes.length > 0 ? Math.round(totalStops / weeklyRoutes.length) : 0,
      averageHoursPerDay: weeklyRoutes.length > 0 ? Math.round((totalHours / weeklyRoutes.length) * 10) / 10 : 0
    };
  }, [weeklyRoutes, crews]);
};

/**
 * Filter and search routes
 * @param {Array} routes - Array of routes
 * @param {Object} filters - Filter criteria
 * @returns {Array} Filtered routes
 */
export const useFilteredRoutes = (routes, filters) => {
  return useMemo(() => {
    if (!routes || routes.length === 0) return [];

    return filterRoutes(routes, filters);
  }, [routes, filters]);
};

/**
 * Calculate route recommendations
 * @param {Array} routes - Current routes
 * @param {Array} unassignedSites - Sites without assignments
 * @param {Array} crews - Available crews
 * @returns {Array} Recommendations
 */
export const useRouteRecommendations = (routes, unassignedSites, crews) => {
  return useMemo(() => {
    if (!routes || !unassignedSites || unassignedSites.length === 0) {
      return [];
    }

    const recommendations = [];

    // Get crew workloads
    const crewWorkloads = {};
    crews.forEach(crew => {
      const crewRoutes = routes.filter(r => r.crewId === crew._id);
      const totalHours = crewRoutes.reduce((sum, route) => {
        const metrics = calculateRouteMetrics(route);
        return sum + metrics.totalHours;
      }, 0);
      crewWorkloads[crew._id] = {
        crew,
        totalHours,
        routeCount: crewRoutes.length
      };
    });

    // Sort crews by workload (ascending)
    const sortedCrews = Object.values(crewWorkloads)
      .sort((a, b) => a.totalHours - b.totalHours);

    // Generate recommendations
    unassignedSites.slice(0, 10).forEach(site => { // Limit to top 10
      const leastBusyCrew = sortedCrews[0];
      if (leastBusyCrew) {
        recommendations.push({
          site,
          recommendedCrew: leastBusyCrew.crew,
          reason: `${leastBusyCrew.crew.name} has the lowest workload (${Math.round(leastBusyCrew.totalHours)}h)`,
          priority: site.priority || 'medium'
        });
      }
    });

    return recommendations;
  }, [routes, unassignedSites, crews]);
};